// src/pages/academy/CreateAcademyCategory.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FolderOpen, Save, ArrowRight, Loader2 } from "lucide-react";
import { academyCategoriesAPI, getApiError } from "../../services/academyService";
import { ACADEMY_ROOT } from "./academyConstants";
import {
  IconField,
  ActiveToggle,
  Field,
  inputClass,
} from "./components/AcademyFormFields";

export default function CreateAcademyCategory() {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const isEdit = Boolean(categoryId);

  const [form, setForm] = useState({
    name: "",
    description: "",
    order: 0,
    is_active: true,
    is_free: false,
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [serverIcon, setServerIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit) fetchCategory();
  }, [categoryId]);

  // تنظيف الـ object URL
  useEffect(() => {
    return () => {
      if (iconPreview) URL.revokeObjectURL(iconPreview);
    };
  }, [iconPreview]);

  const fetchCategory = async () => {
    try {
      const data = await academyCategoriesAPI.getById(categoryId);
      setForm({
        name: data.name || "",
        description: data.description || "",
        order: data.order || 0,
        is_active: data.is_active ?? true,
        is_free: data.is_free ?? false,
      });
      setServerIcon(data.icon_url || null);
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "تعذر تحميل بيانات الكاتيجوري"));
    } finally {
      setFetching(false);
    }
  };

  const pickIcon = (file) => {
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const clearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("اسم الكاتيجوري مطلوب");
      return;
    }
    try {
      setLoading(true);
      setError("");

      const formData = new FormData();
      formData.append("name", form.name.trim());
      formData.append("description", form.description || "");
      formData.append("order", form.order);
      formData.append("is_active", form.is_active ? "true" : "false");
      formData.append("is_free", form.is_free ? "true" : "false");
      if (iconFile) formData.append("icon", iconFile);

      if (isEdit) {
        await academyCategoriesAPI.update(categoryId, formData);
      } else {
        await academyCategoriesAPI.create(formData);
      }
      navigate(ACADEMY_ROOT);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(ACADEMY_ROOT)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <FolderOpen className="w-5 h-5 text-indigo-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? "تعديل الكاتيجوري" : "إضافة كاتيجوري جديدة"}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <IconField
          serverIcon={serverIcon}
          newPreview={iconPreview}
          onPick={pickIcon}
          onClear={clearIcon}
        />

        <Field label="اسم الكاتيجوري" required>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="مثال: Business English"
            className={inputClass}
          />
        </Field>

        <Field label="الوصف">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="وصف مختصر للكاتيجوري..."
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </Field>

        <Field label="الترتيب">
          <input
            type="number"
            min={0}
            value={form.order}
            onChange={(e) =>
              setForm({ ...form, order: parseInt(e.target.value) || 0 })
            }
            className={inputClass}
          />
        </Field>

        <ActiveToggle
          value={form.is_active}
          onChange={(v) => setForm({ ...form, is_active: v })}
        />

        {/* مجاني لكل الطلبة */}
        <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-gray-900">
              مجاني لكل الطلبة
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              لو مفعّل، الكاتيجوري مش هتطلب اشتراك ومش هيظهر لها حد أقصى للأسئلة.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={form.is_free}
            aria-label="مجاني لكل الطلبة"
            onClick={() => setForm({ ...form, is_free: !form.is_free })}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
              form.is_free ? "bg-indigo-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                form.is_free ? "-translate-x-5" : "-translate-x-0.5"
              }`}
            />
          </button>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(ACADEMY_ROOT)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEdit ? "حفظ التعديلات" : "إنشاء الكاتيجوري"}
          </button>
        </div>
      </form>
    </div>
  );
}